import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Radio, Input, Tooltip, Form, InputNumber, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import s from './s.module.less';
import { BillingPolicyType, IBillingPolicy } from 'types/paymentSetup';
import {
    createProspectBilling,
    IProspectBillingInput,
    createProspectTextVisitBilling,
    ITextVisitProspectBillingInput,
} from 'api/prospect';
import { saveOrUpdateInvoice } from 'api/provider';
import commonS from 'styles/common.module.less';
import { IConnectAccountInfoResponse } from 'hooks/useGetConnectAccountInfo';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import dayjs from 'utils/dayjs';
import { EInvoiceChargeType, EInvoiceRelatedType, TSaveOrUpdateInvoice } from 'types/invoice';
import useCurrentUser from 'hooks/useCurrentUser';

interface ChargeModalProps {
    providerId?: number;
    clientId?: number;
    open: boolean;
    onCancel: () => void;
    onSuccess: (amount: number) => void;
    billingPolicy?: IBillingPolicy[];
    connectAccountInfo?: IConnectAccountInfoResponse;
    type?: 'default' | 'prescription-refill';
    textVisitId?: string;
    /** 患者有 Square 信用卡时由 TextVisitUnSubmittedDrawer 传入，此时不根据 connectAccountInfo 禁用选项，并走 create-text-visit 扣款 */
    chargeWithSquareCard?: boolean;
}

const MAIN_OPTIONS = [
    { key: 'appointment', label: 'Appointment penalties' },
    { key: 'service', label: 'Service fees' },
    { key: 'other', label: 'Other fees' },
];

const PRESCRIPTION_REFILL_MAIN_OPTIONS = [
    { key: 'service', label: 'Service fees' },
    { key: 'other', label: 'Other fees' },
];
interface ISub {
    key: string;
    label: string;
    fee?: number | null;
    amountEditable: boolean;
    tooltip: string;
    placeholder: string;
    placeholderPosition?: React.CSSProperties;
}

const TAB_HINT = "Press 'Tab' to use the text";

const ChargeModal: React.FC<ChargeModalProps> = ({
    providerId,
    clientId,
    open,
    onCancel,
    onSuccess,
    billingPolicy,
    connectAccountInfo,
    type,
    textVisitId,
    chargeWithSquareCard,
}) => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const noteInputRef = useRef<any>(null);
    const [mainKey, setMainKey] = useState<string>('');
    const [subKey, setSubKey] = useState<string>('');
    const [homeInfo] = useProviderHomeInfo();
    const isPPAProvider = homeInfo?.ppaProvider;
    const [currentUser] = useCurrentUser();
    const isActingUser = currentUser?.actingUser?.id === currentUser?.user?.id;
    /** PPV（Klarity pay-per-visit）不展示 Appointment penalties，与 PPALayout 的 klarityUser 判定一致 */
    const isPPVProvider = !!homeInfo?.klarityUser;
    const isPrescriptionRefill = !!(type === 'prescription-refill');
    const usedOptions = useMemo(() => {
        if (isPrescriptionRefill || isPPVProvider) return PRESCRIPTION_REFILL_MAIN_OPTIONS;
        return MAIN_OPTIONS;
    }, [isPrescriptionRefill, isPPVProvider]);
    const [appointmentSub, setAppointmentSub] = useState<ISub[]>([
        {
            key: BillingPolicyType.NO_SHOW,
            label: 'No-show fee',
            fee: null,
            amountEditable: false,
            tooltip:
                "Based on your billing policy, if the patient doesn't attend the scheduled appointment within 15 minutes after the appointment start time, a no-show fee of $25.00 may apply.",
            placeholder:
                "Based on the billing policy, if the patient doesn't attend the scheduled appointment within 15 minutes after the appointment start time, a no-show fee of $25.00 may apply.",
            placeholderPosition: {
                top: '98px',
                left: '11px',
            },
        },
        {
            key: BillingPolicyType.LATE_CANCELLATION,
            label: 'Late cancellation fee',
            fee: null,
            amountEditable: false,
            tooltip: 'If the patient cancels late, a late cancellation fee applies.',
            placeholder:
                'Based on the billing policy, if the patient cancels within 24 hours of the appointment, a late cancellation fee of $25.00 may apply.',
            placeholderPosition: {
                top: '98px',
                left: '11px',
            },
        },
        {
            key: BillingPolicyType.RESCHEDULE,
            label: 'Late rescheduling fee',
            fee: null,
            amountEditable: false,
            tooltip: 'If the patient reschedules late, a late rescheduling fee applies.',
            placeholder:
                'Based on the billing policy, if the patient reschedules within 24 hours of the appointment, a late rescheduling fee of $25.00 may apply.',
            placeholderPosition: {
                top: '98px',
                left: '11px',
            },
        },
    ]);
    const [serviceSub] = useState<ISub[]>([
        {
            key: BillingPolicyType.CO_PAY,
            label: 'Co-pay',
            fee: null,
            amountEditable: true,
            tooltip: '',
            placeholder: "Enter reasons for this charge so the patient understands why they're being charged ...",
        },
        {
            key: BillingPolicyType.FULL_SELF_PAYMENT,
            label: 'Full self-pay amount',
            fee: null,
            amountEditable: true,
            tooltip: '',
            placeholder: "Enter reasons for this charge so the patient understands why they're being charged ...",
        },
        {
            key: BillingPolicyType.PARTIAL_PAYMENT,
            label: 'Partial payment amount',
            fee: null,
            amountEditable: true,
            tooltip: '',
            placeholder: 'Please specify the partial payment amount and reason for the charge.',
        },
    ]);
    const [otherSub] = useState<ISub>({
        key: BillingPolicyType.OTHER,
        label: 'Other fees',
        fee: null,
        amountEditable: true,
        tooltip: '',
        placeholder: "Enter reasons for this charge so the patient understands why they're being charged ...",
    });
    const [saveLoading, setSaveLoading] = useState(false);

    useEffect(() => {
        if (billingPolicy) {
            setAppointmentSub((prev) =>
                prev.map((item) => {
                    const policy = billingPolicy.find((p) => p.type === item.key);
                    const fee = policy?.fee;
                    const timeFrame = policy?.timeFrame;
                    const amountEditable = typeof fee === 'number' && !!policy?.enable;
                    const feeNotSetTip = `Unable to charge the client as you haven’t set a ${policy?.type?.toLowerCase()} fee in your billing policies. Update your billing policies to enable this charge. <a href='/payment-setup'>Go to billing policies →</a>`;

                    let tooltip = amountEditable
                        ? `Based on your billing policy, if the patient doesn't attend the scheduled appointment within ${timeFrame} minutes after the appointment start time, a no-show fee of $${fee} may apply.`
                        : feeNotSetTip;
                    let placeholder = `Based on the billing policy, if the patient doesn't attend the scheduled appointment within ${timeFrame} minutes after the appointment start time, a no-show fee of $${fee} may apply.`;

                    if (item.key === BillingPolicyType.LATE_CANCELLATION) {
                        tooltip = amountEditable
                            ? `Based on your billing policy, if the patient need to cancel the appointment with less than ${timeFrame} hours before the appointment notice, a late cancellation fee of $${fee} may apply.`
                            : feeNotSetTip;
                        placeholder = `Based on the billing policy, if the patient need to cancel the appointment with less than ${timeFrame} hours before the appointment notice, a late cancellation fee of $${fee} may apply.`;
                    }
                    if (item.key === BillingPolicyType.RESCHEDULE) {
                        tooltip = amountEditable
                            ? `Based on your billing policy, if the patient reschedule the appointment with less than ${timeFrame} hours' notice, a rescheduling fee of $${fee} may apply.`
                            : feeNotSetTip;
                        placeholder = `Based on the billing policy, if the patient reschedule the appointment with less than ${timeFrame} hours' notice, a rescheduling fee of $${fee} may apply.`;
                    }

                    return {
                        ...item,
                        amountEditable,
                        tooltip,
                        placeholder,
                        fee,
                    };
                }),
            );
        }
    }, [billingPolicy]);

    // 获取当前子选项配置/
    const getCurrentSub = (mainVal?: string, subVal?: string) => {
        if (!mainVal || !subVal) return undefined;
        if (mainVal === 'appointment') return appointmentSub.find((opt) => opt.key === subVal)!;
        if (mainVal === 'service') return serviceSub.find((opt) => opt.key === subVal)!;
        if (mainVal === 'other') return otherSub;
        return appointmentSub[0];
    };

    // 新增：Appointment penalties 独立状态
    const [apAmount, setApAmount] = useState<number | undefined>(undefined);
    const [apNote, setApNote] = useState('');
    const [apAmountError, setApAmountError] = useState('');
    // const [apNoteError, setApNoteError] = useState('');

    // Tab键仅在Appointment penalties下做推荐文案填充
    const handleTabBlock = (e: React.KeyboardEvent) => {
        if (mainKey === 'appointment' && e.key === 'Tab') {
            const currentSub = getCurrentSub(mainKey, subKey);
            if (currentSub && typeof currentSub.fee === 'number') {
                if (!apNote) {
                    setApNote(currentSub.placeholder);
                    if (noteInputRef.current) {
                        noteInputRef.current.focus();
                    }
                }
            }
            e.preventDefault();
        }
    };

    // 校验逻辑（仅Appointment penalties）
    const validateAp = () => {
        let amountErr = '';
        // let noteErr = '';
        const currentSub = getCurrentSub(mainKey, subKey);
        if (currentSub && currentSub.amountEditable) {
            if (apAmount === undefined || apAmount === null || Number.isNaN(apAmount) || apAmount <= 0) {
                amountErr = 'This is a required field';
            }
        }
        // if (!apNote || apNote.trim() === '') {
        //     noteErr = 'This is a required field';
        // }
        setApAmountError(amountErr);
        // setApNoteError(noteErr);
        return !amountErr;
    };

    // 主分组选项切换
    const handleMainChange = (e: any) => {
        const newMain = e.target.value;
        setMainKey(newMain);
        if (newMain === 'appointment') {
            setSubKey(BillingPolicyType.NO_SHOW);
            form.setFieldsValue({
                main: 'appointment',
                sub: BillingPolicyType.NO_SHOW,
                amount_appointment_no_show: appointmentSub[0].fee,
                note_appointment_no_show: '',
            });
        } else if (newMain === 'service') {
            setSubKey(BillingPolicyType.CO_PAY);
            form.setFieldsValue({
                main: 'service',
                sub: BillingPolicyType.CO_PAY,
                amount_service_co_pay: serviceSub[0].fee,
                note_service_co_pay: '',
            });
        } else {
            setSubKey(BillingPolicyType.OTHER);
            form.setFieldsValue({
                main: 'other',
                sub: BillingPolicyType.OTHER,
                amount_other: otherSub.fee,
                note_other: '',
            });
        }
    };

    // 子分组选项切换
    const handleSubChange = (e: any) => {
        setSubKey(e.target.value);
        const mainVal = mainKey;
        const arr = mainVal === 'appointment' ? appointmentSub : serviceSub;
        const subOpt = arr.find((opt) => opt.key === e.target.value);
        if (mainVal === 'appointment') {
            form.setFieldsValue({
                sub: e.target.value,
                amount_appointment_no_show: subOpt?.fee || '',
                note_appointment_no_show: '',
            });
        } else if (mainVal === 'service') {
            form.setFieldsValue({
                sub: e.target.value,
                amount_service_co_pay: subOpt?.fee || '',
                note_service_co_pay: '',
                amount_service_full_self_pay: subOpt?.fee || '',
                note_service_full_self_pay: '',
                amount_service_partial_payment: subOpt?.fee || '',
                note_service_partial_payment: '',
            });
        }
    };

    // 判断Appointment penalties是否全部不可用
    const isAppointmentDisabled = useMemo(() => {
        return billingPolicy?.every((item) => item.enable === false);
    }, [billingPolicy]);

    const hasAnyEnabledMainOption = useMemo(() => {
        if (isPPVProvider) return usedOptions.length > 0;
        return usedOptions.some((mainOpt) => {
            if (mainOpt.key === 'appointment') return !isAppointmentDisabled;
            return true;
        });
    }, [usedOptions, isAppointmentDisabled, isPPVProvider]);

    const isMainRadioDisabled = (mainOptKey: string) => {
        if (isPPVProvider) return false;
        if (mainOptKey === 'appointment') return isAppointmentDisabled;
        return false;
    };

    const isMainSelectionValid = useMemo(() => {
        if (!mainKey) return false;
        if (mainKey === 'appointment') {
            const currentSub = appointmentSub.find((opt) => opt.key === subKey);
            return !!currentSub && currentSub.amountEditable === true;
        }
        if (mainKey === 'service') {
            const currentSub = serviceSub.find((opt) => opt.key === subKey);
            return !!currentSub && currentSub.amountEditable === true;
        }
        if (mainKey === 'other') {
            return subKey === BillingPolicyType.OTHER && otherSub.amountEditable === true;
        }
        return false;
    }, [mainKey, subKey, appointmentSub, serviceSub, otherSub]);

    const isOkDisabled = useMemo(() => {
        return saveLoading || !hasAnyEnabledMainOption || !isMainSelectionValid;
    }, [saveLoading, hasAnyEnabledMainOption, isMainSelectionValid]);

    // 初始化/重置表单
    React.useEffect(() => {
        if (open) {
            if (isAppointmentDisabled || isPrescriptionRefill || isPPVProvider) {
                setMainKey('service');
                setSubKey(BillingPolicyType.CO_PAY);
                form.setFieldsValue({
                    main: 'service',
                    sub: BillingPolicyType.CO_PAY,
                    amount_service_co_pay: '',
                    note_service_co_pay: '',
                    amount_service_full_self_pay: '',
                    note_service_full_self_pay: '',
                    amount_service_partial_payment: '',
                    note_service_partial_payment: '',
                    amount_other: '',
                    note_other: '',
                });
            } else {
                const enabledPolicy = billingPolicy?.find((p) => p.enable);
                setMainKey('appointment');
                setSubKey(enabledPolicy?.type || '');
            }
        } else {
            form.resetFields();
        }
    }, [appointmentSub, form, open, billingPolicy, isAppointmentDisabled, isPrescriptionRefill, isPPVProvider]);

    // 切换主分组时重置Appointment penalties状态
    useEffect(() => {
        if (mainKey === 'appointment' && subKey) {
            const currentSub = appointmentSub.find((opt) => opt.key === subKey);
            setApAmount(typeof currentSub?.fee === 'number' ? currentSub.fee : undefined);
            setApNote('');
            setApAmountError('');
            // setApNoteError('');
        }
    }, [mainKey, subKey, appointmentSub]);

    const handleSave = async (submitData: IProspectBillingInput) => {
        if (!submitData) return;
        const overrideId = !isActingUser ? providerId : undefined;
        setSaveLoading(true);
        if (isPrescriptionRefill && textVisitId) {
            // Create text visit billing and charge with Square CREDIT_CARD (FULL_SELF_PAYMENT 时 chargeAmount 可选)
            const isFullSelfPay = submitData.chargeType === BillingPolicyType.FULL_SELF_PAYMENT;
            const omitAmount = isFullSelfPay && (submitData.chargeAmount == null || submitData.chargeAmount === 0);
            const _data: ITextVisitProspectBillingInput = {
                chargeReason: submitData.chargeReason ?? '',
                chargeType: submitData.chargeType,
                clientId: submitData.clientId,
                textVisitId: textVisitId!,
                ...(omitAmount ? {} : { chargeAmount: submitData.chargeAmount }),
            };
            const res = await createProspectTextVisitBilling(_data, overrideId);
            if (res && !res.error) {
                onSuccess?.(omitAmount ? 0 : submitData.chargeAmount);
            } else {
                // If charging the card fails, automatically send an invoice to the patient.
                try {
                    const amount = omitAmount ? 0 : (submitData.chargeAmount ?? 0);
                    if (clientId && amount > 0) {
                        const params: TSaveOrUpdateInvoice = {
                            clientId,
                            issueDate: dayjs().format('YYYY-MM-DD'),
                            dueDate: dayjs().add(7, 'day').format('YYYY-MM-DD'),
                            noteToPatient:
                                'We attempted to charge your card on file but the payment failed. An invoice has been sent for this prescription refill service.',
                            sendInvoice: true,
                            lineItems: [
                                {
                                    chargeType: EInvoiceChargeType.SERVICE,
                                    description: 'Prescription refill service',
                                    quantity: 1,
                                    unitPrice: amount,
                                    amount,
                                    relatedId: Number(textVisitId),
                                    relatedType: EInvoiceRelatedType.TEXT_VISIT,
                                },
                            ],
                        };
                        const invoiceRes = await saveOrUpdateInvoice(params, overrideId);
                        if (!invoiceRes.error) {
                            message.success('Payment failed. Invoice sent to patient.');
                        } else {
                            message.error(res?.error || invoiceRes.error || 'Payment failed.');
                        }
                    } else {
                        message.error(res?.error || 'Payment failed.');
                    }
                } catch (e) {
                    console.error(e);
                    message.error(res?.error || 'Payment failed.');
                }
            }
            setSaveLoading(false);
            return;
        }
        try {
            const res = await createProspectBilling(submitData, overrideId);
            if (res && !res.error) {
                onSuccess?.(submitData.chargeAmount);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSaveLoading(false);
        }
    };

    // 提交
    const handleSubmit = () => {
        if (!providerId || !clientId) {
            message.error('Please select a client first');
            return;
        }
        if (mainKey === 'appointment') {
            if (validateAp()) {
                handleSave({
                    providerId,
                    clientId,
                    chargeType: subKey,
                    chargeAmount: apAmount !== undefined ? apAmount : getCurrentSub(mainKey, subKey)?.fee || 0,
                    chargeReason: apNote,
                });
            }
        } else {
            form.validateFields().then((values) => {
                const { main } = values;
                const { sub } = values;
                let fee = 0;
                let note = '';
                if (main === 'other') {
                    fee = values.amount_other;
                    note = values.note_other;
                } else {
                    fee = values[`amount_${main}_${sub}`];
                    note = values[`note_${main}_${sub}`];
                }

                handleSave({
                    providerId,
                    clientId,
                    chargeType: main === 'other' ? BillingPolicyType.OTHER : sub,
                    chargeAmount: fee ?? 0,
                    chargeReason: note,
                });
            });
        }
    };

    const handleCancel = () => {
        onCancel();
        form.resetFields();
        setApAmount(undefined);
        setApNote('');
        setApAmountError('');
        if (isPPVProvider || isPrescriptionRefill) {
            setMainKey('service');
            setSubKey(BillingPolicyType.CO_PAY);
        } else {
            setMainKey('appointment');
            setSubKey(BillingPolicyType.NO_SHOW);
        }
    };

    const renderSubGroup = (groupType: 'service') => {
        const options = serviceSub;
        return (
            <Radio.Group value={subKey} onChange={handleSubChange} className={s.subGroup}>
                {options.map((opt) => {
                    const checked = subKey === opt.key;
                    return (
                        <div className={`${checked ? s.cardActive : s.card}`} key={opt.key}>
                            <div className={s.cardHeader}>
                                <div className={s.labelWrap}>
                                    <Radio value={opt.key} className={s.radioBtn} />
                                    <span className={s.optionLabel}>{opt.label}</span>
                                    {opt.tooltip && (
                                        <Tooltip
                                            color="#FFFFFF"
                                            overlayInnerStyle={{ padding: '12px' }}
                                            title={
                                                <div
                                                    className={s.tooltipContent}
                                                    dangerouslySetInnerHTML={{ __html: opt.tooltip }}
                                                />
                                            }
                                            placement="top"
                                        >
                                            <QuestionCircleOutlined className={s.qIcon} />
                                        </Tooltip>
                                    )}
                                </div>
                                {opt.amountEditable &&
                                    !(
                                        isPrescriptionRefill &&
                                        chargeWithSquareCard &&
                                        opt.key === BillingPolicyType.FULL_SELF_PAYMENT
                                    ) && (
                                        <Form.Item
                                            name={`amount_${groupType}_${opt.key}`}
                                            className={s.amountInputWrap}
                                            rules={[
                                                {
                                                    required: opt.amountEditable && checked,
                                                    message: 'This is a required field',
                                                },
                                            ]}
                                        >
                                            <InputNumber addonBefore="$" value={opt.fee} className={s.amountInput} />
                                        </Form.Item>
                                    )}
                            </div>
                            {checked && (
                                <div className={s.cardBody}>
                                    <div className={s.reasonTitle}>Reason for the charge</div>
                                    <Form.Item
                                        name={`note_${type}_${opt.key}`}
                                        rules={[{ required: true, message: 'This is a required field' }]}
                                        initialValue=""
                                    >
                                        <Input.TextArea
                                            style={{ resize: 'none' }}
                                            placeholder={opt.placeholder}
                                            className={s.noteInput}
                                        />
                                    </Form.Item>
                                </div>
                            )}
                        </div>
                    );
                })}
            </Radio.Group>
        );
    };

    const renderOther = () => (
        <div className={s.cardActive}>
            <div className={s.cardHeader}>
                <Form.Item name="amount_other" rules={[{ required: true, message: 'This is a required field' }]}>
                    <InputNumber addonBefore="$" className={s.amountInput} />
                </Form.Item>
            </div>
            <div className={s.cardBody}>
                <div className={s.reasonTitle}>Reason for the charge</div>
                <Form.Item name="note_other" rules={[{ required: true, message: 'This is a required field' }]}>
                    <Input.TextArea
                        style={{ resize: 'none' }}
                        placeholder={otherSub.placeholder}
                        className={s.noteInput}
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        value={(() => {
                            const v = form.getFieldValue('note_other');
                            if (typeof v === 'string' || typeof v === 'number') return v;
                            return '';
                        })()}
                    />
                </Form.Item>
            </div>
        </div>
    );

    // 避免和form中tab切换焦点的行为冲突，所以appointment patien不使用Form
    return (
        <div tabIndex={-1} onKeyDown={handleTabBlock}>
            <Modal
                className={commonS.modalFixHeightWrap}
                width={560}
                title={isPrescriptionRefill ? null : 'Charge client'}
                open={open}
                onCancel={handleCancel}
                destroyOnClose
                okText="Charge client"
                okButtonProps={{ disabled: isOkDisabled }}
                onOk={handleSubmit}
                confirmLoading={saveLoading}
            >
                <div className={s.mWrap}>
                    {isPrescriptionRefill && (
                        <div className={s.mHeader}>
                            <div className={s.mHeaderIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M20.0501 6.35147C20.5187 6.8201 20.5187 7.5799 20.0501 8.04853L10.4501 17.6485C9.98146 18.1172 9.22166 18.1172 8.75303 17.6485L3.95303 12.8485C3.48441 12.3799 3.48441 11.6201 3.95303 11.1515C4.42166 10.6828 5.18146 10.6828 5.65009 11.1515L9.60156 15.1029L18.353 6.35147C18.8217 5.88284 19.5815 5.88284 20.0501 6.35147Z"
                                        fill="#059669"
                                        stroke="#059669"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <div className={s.mHeaderTitle}>Prescription refill request completed successfully</div>
                            <div className={s.mHeaderDes}>
                                The medication has been prescribed and sent to the pharmacy. You can charge the patient
                                now or do it later.
                            </div>
                        </div>
                    )}
                    <div className={`${s.mContent} ${isPrescriptionRefill ? s.mContentWithHeader : ''}`}>
                        <div className={s.mContentTilte}>Charge client</div>
                        <div className={s.sectionTitle}>Choose the reason you&apos;re charging the patient</div>
                        {mainKey === 'appointment' ? (
                            <div className={s.formWrap}>
                                <Radio.Group value={mainKey} onChange={handleMainChange} className={s.mainGroup}>
                                    {usedOptions.map((mainOpt) => {
                                        return (
                                            <div className={s.mainRow} key={mainOpt.key}>
                                                <div className={s.labelRow}>
                                                    <Radio
                                                        value={mainOpt.key}
                                                        className={s.radioBtn}
                                                        disabled={isMainRadioDisabled(mainOpt.key)}
                                                    />
                                                    <span className={s.mainLabel}>{mainOpt.label}</span>
                                                </div>
                                                {mainOpt.key === mainKey && (
                                                    <div className={s.subGroupRow}>
                                                        <div className={s.subGroupWrap}>
                                                            <Radio.Group
                                                                value={subKey}
                                                                onChange={handleSubChange}
                                                                className={s.subGroup}
                                                            >
                                                                {appointmentSub.map((subOpt) => {
                                                                    const checked = subKey === subOpt.key;
                                                                    const enable = subOpt.amountEditable;
                                                                    return (
                                                                        <div
                                                                            className={`${checked ? s.cardActive : s.card} ${!enable ? s.disabledCard : ''}`}
                                                                            key={subOpt.key}
                                                                        >
                                                                            <div className={s.cardHeader}>
                                                                                <div className={s.labelWrap}>
                                                                                    <Radio
                                                                                        disabled={!enable}
                                                                                        value={enable ? subOpt.key : ''}
                                                                                        className={s.radioBtn}
                                                                                    />
                                                                                    <span className={s.optionLabel}>
                                                                                        {subOpt.label}
                                                                                    </span>
                                                                                    {subOpt.tooltip && (
                                                                                        <Tooltip
                                                                                            color="#FFFFFF"
                                                                                            overlayInnerStyle={{
                                                                                                padding: '12px',
                                                                                            }}
                                                                                            title={
                                                                                                <div
                                                                                                    className={
                                                                                                        s.tooltipContent
                                                                                                    }
                                                                                                    dangerouslySetInnerHTML={{
                                                                                                        __html: subOpt.tooltip,
                                                                                                    }}
                                                                                                />
                                                                                            }
                                                                                            placement="top"
                                                                                        >
                                                                                            <QuestionCircleOutlined
                                                                                                className={s.qIcon}
                                                                                            />
                                                                                        </Tooltip>
                                                                                    )}
                                                                                </div>
                                                                                {enable && (
                                                                                    <InputNumber
                                                                                        addonBefore="$"
                                                                                        value={subOpt.fee}
                                                                                        onChange={
                                                                                            enable
                                                                                                ? (
                                                                                                      value:
                                                                                                          | number
                                                                                                          | null,
                                                                                                  ) => {
                                                                                                      form.setFieldsValue(
                                                                                                          {
                                                                                                              [`amount_appointment_${subOpt.key}`]:
                                                                                                                  value ===
                                                                                                                  null
                                                                                                                      ? undefined
                                                                                                                      : value,
                                                                                                          },
                                                                                                      );
                                                                                                  }
                                                                                                : undefined
                                                                                        }
                                                                                        className={s.amountInput}
                                                                                        disabled
                                                                                        status={
                                                                                            apAmountError ? 'error' : ''
                                                                                        }
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                            {checked && (
                                                                                <div className={s.cardBody}>
                                                                                    <div className={s.reasonTitle}>
                                                                                        Reason for the charge{' '}
                                                                                        <span className={s.optional}>
                                                                                            (optional)
                                                                                        </span>
                                                                                    </div>
                                                                                    <Input.TextArea
                                                                                        style={{ resize: 'none' }}
                                                                                        placeholder={subOpt.placeholder}
                                                                                        ref={noteInputRef}
                                                                                        value={
                                                                                            typeof apNote === 'string'
                                                                                                ? apNote
                                                                                                : ''
                                                                                        }
                                                                                        onChange={(e) =>
                                                                                            setApNote(e.target.value)
                                                                                        }
                                                                                        className={s.noteInput}
                                                                                        // status={apNoteError ? 'error' : ''}
                                                                                    />
                                                                                    {/* <QuillRichText
                                                                                    hideToolbar
                                                                                    quillRef={noteInputRef}
                                                                                    plcaeholder={subOpt.placeholder}
                                                                                    value={apNote}
                                                                                    onChange={(value) => setApNote(value)}
                                                                                />
                                                                                {/* {apNoteError && <div className={s.errorTip}>{apNoteError}</div>} */}
                                                                                    {!apNote && (
                                                                                        <div
                                                                                            className={s.tabTip}
                                                                                            style={
                                                                                                subOpt.placeholderPosition
                                                                                            }
                                                                                        >
                                                                                            {TAB_HINT}
                                                                                        </div>
                                                                                    )}
                                                                                    {/* { lateCancelFeeNote && <div className={s.tabTip}>Press "Tab" to use the text</div> }
                                                                                { lateRescheduleFeeNote && <div className={s.tabTip}>Press "Tab" to use the text</div> } */}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </Radio.Group>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </Radio.Group>
                            </div>
                        ) : (
                            <Form form={form} layout="vertical">
                                <div className={s.formWrap}>
                                    <Form.Item name="main" noStyle>
                                        <Radio.Group
                                            value={mainKey}
                                            onChange={handleMainChange}
                                            className={s.mainGroup}
                                        >
                                            {usedOptions.map((mainOpt) => {
                                                return (
                                                    <div className={s.mainRow} key={mainOpt.key}>
                                                        <div className={s.labelRow}>
                                                            <Radio
                                                                value={mainOpt.key}
                                                                className={s.radioBtn}
                                                                disabled={isMainRadioDisabled(mainOpt.key)}
                                                            />
                                                            <span className={s.mainLabel}>{mainOpt.label}</span>
                                                        </div>
                                                        {mainOpt.key === 'appointment' && isAppointmentDisabled && (
                                                            <div className={s.warning}>
                                                                <div className={s.wrongIcon} />
                                                                <div className={s.warningText}>
                                                                    <div className={s.text}>
                                                                        Unable to charge the client for appointment
                                                                        penalties as you haven&apos;t set up your
                                                                        billing policies yet.
                                                                    </div>
                                                                    <div
                                                                        className={s.link}
                                                                        onClick={() => {
                                                                            navigate(`/${PATH.PAYMENT_SETUP}`);
                                                                        }}
                                                                    >
                                                                        Set up billing policies →
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {mainOpt.key === mainKey && (
                                                            <div className={s.subGroupRow}>
                                                                <div className={s.subGroupWrap}>
                                                                    {mainOpt.key === 'other'
                                                                        ? renderOther()
                                                                        : renderSubGroup(mainOpt.key as 'service')}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </Radio.Group>
                                    </Form.Item>
                                    <Form.Item name="sub" noStyle>
                                        {mainKey !== 'other' && (
                                            <Radio.Group
                                                value={subKey}
                                                onChange={handleSubChange}
                                                style={{ display: 'none' }}
                                            />
                                        )}
                                    </Form.Item>
                                </div>
                            </Form>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ChargeModal;
